import { chooseLoadBalancedEnvironment, type LoadBalancingCandidate } from "@afar-research/afar";
import {
  type EnvironmentId,
  type HostResourcesSnapshot,
  WS_METHODS,
} from "@afar-research/contracts";
import * as Clock from "effect/Clock";
import * as Effect from "effect/Effect";
import type { Atom } from "effect/unstable/reactivity";

import { EnvironmentRegistry } from "../connection/registry.ts";
import { request } from "../rpc/client.ts";
import { createAtomCommandScheduler, createRuntimeCommand } from "./runtime.ts";

/**
 * How long a machine gets to answer before it is left out of this placement. A machine that
 * cannot say how busy it is within a moment is not a machine to hand a new turn to, and waiting
 * on it would stall the composer for every other candidate as well.
 */
const RESOURCE_READ_TIMEOUT = "2 seconds";

export interface LoadBalancingRequestCandidate {
  readonly environmentId: EnvironmentId;
  readonly weight: number;
}

/**
 * Reads one machine's capacity, or nothing. Failure is an ordinary answer here: a machine that
 * is disconnected, too old to know the method, or simply slow is one this placement skips, not
 * one that fails the placement for everybody.
 */
const readHostResources = Effect.fn("loadBalancing.readHostResources")(function* (
  environmentId: EnvironmentId,
) {
  const registry = yield* EnvironmentRegistry;
  const snapshot = yield* registry
    .run(environmentId, request(WS_METHODS.serverGetHostResources, {}))
    .pipe(
      Effect.timeout(RESOURCE_READ_TIMEOUT),
      Effect.option,
      Effect.map((option) => (option._tag === "Some" ? option.value : null)),
    );
  if (snapshot === null) return null;
  // Receipt time, not the machine's own `sampledAt`: two machines' clocks cannot be compared,
  // and a machine whose clock is wrong would otherwise look permanently fresh or permanently stale.
  return {
    environmentId,
    resources: snapshot satisfies HostResourcesSnapshot,
    receivedAt: yield* Clock.currentTimeMillis,
  };
});

export function createLoadBalancingAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  const scheduler = createAtomCommandScheduler();

  /**
   * Picks where a new thread should run, or null when no candidate could prove it has room.
   *
   * Null means "ask the user". Callers pass only machines that are connected, host the project,
   * and carry the selected Agent Provider; capacity is the only thing decided here.
   */
  const chooseEnvironment = createRuntimeCommand(runtime, {
    label: "load-balancing:choose-environment",
    scheduler,
    // One placement at a time. Two composers deciding at once would each read every machine.
    concurrency: { mode: "serial", key: () => "load-balancing" },
    execute: (candidates: ReadonlyArray<LoadBalancingRequestCandidate>) =>
      Effect.gen(function* () {
        const eligible = candidates.filter((candidate) => candidate.weight > 0);
        if (eligible.length === 0) return null;
        // A single machine needs no evidence: it is the only place the work can go.
        if (eligible.length === 1) return eligible[0]!.environmentId;
        const readings = yield* Effect.forEach(
          eligible,
          (candidate) => readHostResources(candidate.environmentId),
          { concurrency: "unbounded" },
        );
        const now = yield* Clock.currentTimeMillis;
        const weighted: ReadonlyArray<LoadBalancingCandidate> = eligible.map((candidate, index) => {
          const reading = readings[index];
          return {
            environmentId: candidate.environmentId,
            weight: candidate.weight,
            resources: reading?.resources ?? null,
            ...(reading ? { receivedAt: reading.receivedAt } : {}),
          };
        });
        return chooseLoadBalancedEnvironment(weighted, now) as EnvironmentId | null;
      }),
  });

  return { chooseEnvironment };
}
